import { createSlice, freeze, type PayloadAction } from '@reduxjs/toolkit';
import type { OptionMarketTabData } from '@src/providers/MarketTabs.types';
import {
  EMPTY_OBJECT,
  type AmountObject,
  type MarketAccount,
  type RangeSelectObject,
  type WebsocketError,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { DEFAULT_OPTION_COLUMNS_IS_VISIBLE } from '../types';
import { getVisibilityMappingForMarket } from '../utils';
import type { OptionFilteringMeta, OptionFilteringPanelState, OptionFilteringState } from './types';
import { validateSymbol } from './utils';

export const initialOptionFilterState: OptionFilteringState = {
  meta: {
    expirationByMarketByCurrencyIdentity: new Map(),
    optionSecurityBySymbol: new Map(),
    marketAccountsByMarket: new Map(),
  },
  panels: {},
};

export const initialOptionFilterPanelState: OptionFilteringPanelState = {
  underlyingAmountByExpiry: {},
  loading: true,
  columnDefsVisibility: DEFAULT_OPTION_COLUMNS_IS_VISIBLE,
  error: {},
  optionFilter: {
    currency: 'BTC',
    market: 'deribit',
    expiry: null,
    marketAccountName: null,
  },
  strikeFilter: {},
};

function initPanelState(state: WritableDraft<OptionFilteringState>, panelID: string) {
  const panelState: WritableDraft<OptionFilteringPanelState> =
    state.panels[panelID] ?? structuredClone(initialOptionFilterPanelState);
  state.panels[panelID] = panelState;
  return panelState;
}

function resetColumnDefsVisibilityState(panel: WritableDraft<OptionFilteringPanelState>) {
  const visibilityMapping =
    getVisibilityMappingForMarket(panel.optionFilter.market) ?? DEFAULT_OPTION_COLUMNS_IS_VISIBLE;

  panel.columnDefsVisibility = visibilityMapping;
}

const filterSlice = createSlice({
  name: 'filter',
  initialState: initialOptionFilterState,
  reducers: {
    updateMeta: (state, action: PayloadAction<OptionFilteringMeta>) => {
      state.meta = freeze(action.payload);
    },
    hydratePersistedTabState: (state, action: PayloadAction<Partial<OptionMarketTabData> & { panelID: string }>) => {
      const panel = initPanelState(state, action.payload.panelID);
      panel.strikeFilter = action.payload.strikeFilter || EMPTY_OBJECT;
      panel.optionFilter = validateSymbol(action.payload.optionFilter || panel.optionFilter, state.meta);

      // If there is some column def state persisted, use that. Otherwise, reset to the default for the panel state.
      if (action.payload.columnDefsVisibility) {
        panel.columnDefsVisibility = action.payload.columnDefsVisibility;
      } else {
        resetColumnDefsVisibilityState(panel);
      }

      panel.loading = false;
    },
    /** Update the panel symbol (typically from the Options dropdown) using the currency and underlying (currency or derivative underlying) */
    updatePanelSymbol: (
      state,
      action: PayloadAction<{ currency: string; underlyingCode: string; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);

      const { currency, underlyingCode } = action.payload;
      if (panel.optionFilter.underlyingCode !== underlyingCode || panel.optionFilter.currency !== currency) {
        panel.optionFilter.currency = currency;
        panel.optionFilter.underlyingCode = underlyingCode;
        // If changing symbol, remove strike range filtering
        delete panel.strikeFilter.strikePrice;
        panel.underlyingAmountByExpiry = {};
      }

      panel.optionFilter = validateSymbol(panel.optionFilter, state.meta);
    },

    updateMarket: (state, action: PayloadAction<{ market: string; panelID: string }>) => {
      const panel = initPanelState(state, action.payload.panelID);

      panel.optionFilter.market = action.payload.market;
      panel.optionFilter.underlyingCode = panel.optionFilter.currency;
      panel.optionFilter = validateSymbol(panel.optionFilter, state.meta);

      // When the market changes, we reset columns. Deribit, CME, FalconX all have different sets of relevant columns
      resetColumnDefsVisibilityState(panel);
    },
    updateMarketAccount: (
      state,
      action: PayloadAction<{ marketAccount: MarketAccount | undefined; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);
      if (action.payload.marketAccount?.MarketAccountID) {
        panel.optionFilter.marketAccountName = action.payload.marketAccount.Name;
      }
    },
    updateExpiry: (state, action: PayloadAction<{ expiry: string; panelID: string }>) => {
      const panel = initPanelState(state, action.payload.panelID);
      panel.optionFilter.expiry = action.payload.expiry;
    },
    // UnderlyingPrice
    updateUnderlyingAmount: (
      state,
      action: PayloadAction<{ expiry: string; value: AmountObject | null; baseCurrency?: string; panelID: string }>
    ) => {
      const { value, expiry, baseCurrency, panelID } = action.payload;
      const panel = initPanelState(state, panelID);
      if (value === null) {
        delete panel.underlyingAmountByExpiry[expiry];
        return;
      }
      // Ensure that updates to underlying is done to the same currency it was sent to update.
      if (baseCurrency === panel.optionFilter.currency) {
        panel.underlyingAmountByExpiry[expiry] = value;
      }
    },
    // strikes
    updateATM: (state, action: PayloadAction<{ atm: number | null; panelID: string }>) => {
      const panel = initPanelState(state, action.payload.panelID);
      panel.strikeFilter = action.payload.atm ? { atm: action.payload.atm } : {};
    },
    updateStrikeRange: (
      state,
      action: PayloadAction<{ strikeRange: RangeSelectObject | undefined; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);
      if (action.payload.strikeRange === undefined) {
        panel.strikeFilter.strikePrice = undefined;
        return;
      }
      panel.strikeFilter = {
        strikePrice: {
          to: action.payload.strikeRange.to ?? panel.strikeFilter.strikePrice?.to,
          from: action.payload.strikeRange.from ?? panel.strikeFilter.strikePrice?.from,
        },
      };

      if (panel.strikeFilter.strikePrice) {
        if (action.payload.strikeRange.from === null) {
          delete panel.strikeFilter.strikePrice.from;
        }
        if (action.payload.strikeRange.to === null) {
          delete panel.strikeFilter.strikePrice.to;
        }
      }
    },
    updateDeltaRange: (
      state,
      action: PayloadAction<{ deltaRange: RangeSelectObject | undefined; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);
      if (action.payload === undefined || action.payload.deltaRange === undefined) {
        panel.strikeFilter.delta = undefined;
        return;
      }
      panel.strikeFilter = {
        delta: {
          to: action.payload.deltaRange?.to ?? panel.strikeFilter.delta?.to,
          from: action.payload.deltaRange?.from ?? panel.strikeFilter.delta?.from,
        },
      };

      if (panel.strikeFilter.delta) {
        if (action.payload.deltaRange?.from === null) {
          delete panel.strikeFilter.delta.from;
        }
        if (action.payload.deltaRange?.to === null) {
          delete panel.strikeFilter.delta.to;
        }
      }
    },
    updateColumnDefsVisibility: (
      state,
      action: PayloadAction<{ columnDefsVisibility: Record<string, boolean>; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);
      panel.columnDefsVisibility = action.payload.columnDefsVisibility;
    },
    updateMarketDataError: (
      state,
      action: PayloadAction<{ error: WebsocketError['msg'] | undefined; panelID: string }>
    ) => {
      const panel = initPanelState(state, action.payload.panelID);
      panel.error.marketData = action.payload.error;
    },
  },
});

export const {
  updateMarket,
  updateExpiry,
  updateMeta,
  updatePanelSymbol,
  updateATM,
  updateStrikeRange,
  updateDeltaRange,
  hydratePersistedTabState,
  updateUnderlyingAmount,
  updateMarketAccount,
  updateColumnDefsVisibility,
  updateMarketDataError,
} = filterSlice.actions;

export const filterSliceReducer = filterSlice.reducer;
